import ApiClient from "../api";
import { partialRight } from "lodash";
import * as Shop from "../types/Shop";
import App from "../types/App";
import {
  processShopifyResponse,
  processShopifyErrorResponse,
} from "../api/utils/api";
import {
  queryCollectionByHandle,
  queryMultipleCollections,
} from "../utils/shop";
import { SLEEP_GOAL_COLLECTION_QUERY_SEARCH } from "../utils/collectionConstants";
import { INVALID_DISCOUNT_CODE_ERROR } from "../utils/errorMessages";

export const showCart = () => {
  return { type: Shop.SHOW_CART };
};

export const hideCart = () => {
  return { type: Shop.HIDE_CART };
};

export const receivedStaleCheckout = () => ({ type: Shop.STALE_CHECKOUT_ID });

export const fetchCheckoutFailure = (errors) => {
  return (dispatch) => {
    dispatch({
      type: Shop.FETCH_CHECKOUT_REQUEST_FAILURE,
      errors,
    });
    dispatch({
      type: App.APP_ERROR,
      errors,
    });
  };
};

export const fetchCheckoutReceive = (resp) => {
  return {
    type: Shop.FETCH_CHECKOUT_RECEIVE,
    checkout: resp,
  };
};

export const fetchCheckoutRequest = (checkoutId) => {
  return (dispatch) => {
    dispatch({ type: Shop.FETCH_CHECKOUT_REQUEST });

    return ApiClient.getShopify()
      .checkout.fetch(checkoutId)
      .then((res) => {
        // If no checkout object (stale checkout) OR
        // a checkout that has been completed have been returned,
        // fire off stale checkout ID action to request
        // a new one
        if (!res || (res && res.completedAt)) {
          // Stale checkout ID
          return dispatch(receivedStaleCheckout());
        }
        return dispatch(processShopifyResponse(res, fetchCheckoutReceive));
      })
      .catch((err) =>
        dispatch(processShopifyErrorResponse(err, fetchCheckoutFailure))
      );
  };
};

export const createCheckoutRequestFailure = (errors) => {
  return (dispatch) => {
    dispatch({
      type: Shop.CREATE_CHECKOUT_REQUEST_FAILURE,
      errors,
    });

    dispatch({
      type: App.APP_ERROR,
      errors,
    });
  };
};

export const createCheckoutReceive = (resp) => {
  return {
    type: Shop.CREATE_CHECKOUT_RECEIVE,
    checkout: resp,
  };
};

export const createCheckoutRequest = (input = {}) => {
  return (dispatch) => {
    dispatch({ type: Shop.CREATE_CHECKOUT_REQUEST });

    return ApiClient.getShopify()
      .checkout.create(input)
      .then((res) =>
        dispatch(processShopifyResponse(res, createCheckoutReceive))
      )
      .catch((err) =>
        dispatch(processShopifyErrorResponse(err, createCheckoutRequestFailure))
      );
  };
};

export const updateCheckoutRequestFailure = (errors) => {
  return (dispatch) => {
    dispatch({
      type: Shop.UPDATE_CHECKOUT_REQUEST_FAILURE,
      errors,
    });

    dispatch({
      type: App.APP_ERROR,
      errors,
    });
  };
};

export const updateCheckoutReceive = (resp) => {
  return {
    type: Shop.UPDATE_CHECKOUT_RECEIVE,
    checkout: resp,
  };
};

export const updateCheckoutRequest = (checkoutId, input = {}) => {
  return (dispatch) => {
    dispatch({ type: Shop.UPDATE_CHECKOUT_REQUEST });

    return ApiClient.getShopify()
      .checkout.updateAttributes(checkoutId, input)
      .then((res) =>
        dispatch(processShopifyResponse(res, updateCheckoutReceive))
      )
      .catch((err) =>
        dispatch(processShopifyErrorResponse(err, updateCheckoutRequestFailure))
      );
  };
};

export const addToCartRequestFailure = (errors) => {
  return (dispatch) => {
    dispatch({ type: Shop.ADD_TO_CART_REQUEST_FAILURE });
    dispatch({
      type: App.APP_ERROR,
      errors,
    });
  };
};

export const addToCartReceive = (checkout, variantIdAdded) => {
  return {
    type: Shop.ADD_TO_CART_RECEIVE,
    checkout,
    variantId: variantIdAdded,
  };
};

export const addToCartRequest = ({
  variantId,
  checkoutId,
  quantity,
  customAttributes,
}) => {
  const lineItemsToAdd = [
    {
      variantId,
      quantity: parseInt(quantity, 10),
      customAttributes,
    },
  ];

  return (dispatch) => {
    dispatch({ type: Shop.SHOW_CART });
    dispatch({ type: Shop.ADD_TO_CART_REQUEST, variantId });
    return ApiClient.getShopify()
      .checkout.addLineItems(checkoutId, lineItemsToAdd)
      .then((res) =>
        dispatch(
          processShopifyResponse(res, partialRight(addToCartReceive, variantId))
        )
      )
      .catch((err) =>
        dispatch(processShopifyErrorResponse(err, addToCartRequestFailure))
      );
  };
};

export const removeFromCartRequestFailure = (errors) => {
  return (dispatch) => {
    dispatch({ type: Shop.REMOVE_FROM_CART_REQUEST_FAILURE });
    dispatch({
      type: App.APP_ERROR,
      errors,
    });
  };
};

export const removeFromCartReceive = (resp, variantId, lineItemId) => {
  return {
    type: Shop.REMOVE_FROM_CART_RECEIVE,
    checkout: resp,
    variantId,
    lineItemId,
  };
};

export const removeFromCartRequest = (checkoutId, lineItemId, variantId) => {
  return (dispatch) => {
    dispatch({ type: Shop.REMOVE_FROM_CART_REQUEST });

    return ApiClient.getShopify()
      .checkout.removeLineItems(checkoutId, [lineItemId])
      .then((res) =>
        dispatch(
          processShopifyResponse(
            res,
            partialRight(removeFromCartReceive, variantId, lineItemId)
          )
        )
      )
      .catch((err) =>
        dispatch(processShopifyErrorResponse(err, removeFromCartRequestFailure))
      );
  };
};

export const updateQuantityRequestFailure = (errors, lineItemId) => {
  return (dispatch) => {
    dispatch({ type: Shop.UPDATE_QUANTITY_REQUEST_FAILURE });

    // Cancel out the loading state since the request failed
    dispatch({ type: Shop.COMPLETE_QUANTITY_UPDATE, lineItemId });
    dispatch({
      type: App.APP_ERROR,
      errors,
    });
  };
};

export const updateQuantityReceive = (resp, lineItemId) => {
  return (dispatch) => {
    dispatch({ type: Shop.UPDATE_QUANTITY_RECEIVE, checkout: resp });
    dispatch({ type: Shop.COMPLETE_QUANTITY_UPDATE, lineItemId });
  };
};

export const updateQuantityRequest = (checkoutId, lineItemId, quantity) => {
  const lineItemsToUpdate = [
    { id: lineItemId, quantity: parseInt(quantity, 10) },
  ];

  return (dispatch) => {
    dispatch({
      type: Shop.UPDATE_QUANTITY_REQUEST,
      checkoutId,
      lineItemId,
      quantity,
    });
    dispatch({ type: Shop.START_QUANTITY_UPDATE, lineItemId: lineItemId });
    return ApiClient.getShopify()
      .checkout.updateLineItems(checkoutId, lineItemsToUpdate)
      .then((res) =>
        dispatch(
          processShopifyResponse(
            res,
            partialRight(updateQuantityReceive, lineItemId)
          )
        )
      )
      .catch((err) =>
        dispatch(
          processShopifyErrorResponse(
            err,
            partialRight(updateQuantityRequestFailure, lineItemId)
          )
        )
      );
  };
};

export const fetchCollectionFailure = (errors, handle) => {
  return (dispatch) => {
    dispatch({
      type: Shop.FETCH_COLLECTION_REQUEST_FAILURE,
      errors,
      handle,
    });
    dispatch({
      type: App.APP_ERROR,
      errors,
    });
  };
};

export const fetchCollectionReceive = (resp, handle) => ({
  type: Shop.FETCH_COLLECTION_RECEIVE,
  collection: resp,
  handle,
});

export const fetchCollectionRequest = (handle) => {
  return (dispatch) => {
    dispatch({ type: Shop.FETCH_COLLECTION_REQUEST, handle });

    // TODO: figure out desired error handling
    return queryCollectionByHandle(handle)
      .then((result) =>
        dispatch(
          processShopifyResponse(
            result,
            partialRight(fetchCollectionReceive, handle)
          )
        )
      )
      .catch((error) =>
        dispatch(processShopifyErrorResponse(error, fetchCollectionFailure))
      );
  };
};

export const fetchSleepGoalSubCollectionsFailure = (errors) => {
  return (dispatch) => {
    dispatch({
      type: Shop.FETCH_SLEEP_GOAL_SUB_COLLECTIONS_FAILURE,
      errors,
    });
    dispatch({
      type: App.APP_ERROR,
      errors,
    });
  };
};

export const fetchSleepGoalSubCollectionsReceive = (collections) => ({
  type: Shop.FETCH_SLEEP_GOAL_SUB_COLLECTIONS_RECEIVE,
  collections,
});

export const fetchSleepGoalSubCollectionsRequest = (
  searchTerm = SLEEP_GOAL_COLLECTION_QUERY_SEARCH
) => {
  return (dispatch) => {
    dispatch({
      type: Shop.FETCH_SLEEP_GOAL_SUB_COLLECTIONS_REQUEST,
      searchTerm,
    });

    return queryMultipleCollections(searchTerm)
      .then((result) =>
        dispatch(
          processShopifyResponse(
            result,
            partialRight(fetchSleepGoalSubCollectionsReceive, searchTerm)
          )
        )
      )
      .catch((error) =>
        dispatch(
          processShopifyErrorResponse(
            error,
            fetchSleepGoalSubCollectionsFailure
          )
        )
      );
  };
};

export const applyDiscountCodeReceive = (checkout) => ({
  type: Shop.APPLY_DISCOUNT_CODE_RECEIVE,
  checkout,
});

export const applyDiscountCodeFailure = (errors) => {
  return (dispatch) => {
    dispatch({ type: App.APP_ERROR, errors });

    dispatch({ type: Shop.APPLY_DISCOUNT_CODE_FAILURE, errors });
  };
};

export const invalidDiscountCodeApplied = () => ({
  type: Shop.INVALID_DISCOUNT_CODE_APPLIED,
  errors: INVALID_DISCOUNT_CODE_ERROR,
});

export const applyDiscountCodeRequest = (checkoutId, discountCode) => {
  return (dispatch) => {
    dispatch({ type: Shop.APPLY_DISCOUNT_CODE_REQUEST });

    // https://shopify.github.io/js-buy-sdk/#adding-a-discount
    return ApiClient.getShopify()
      .checkout.addDiscount(checkoutId, discountCode)
      .then((res) => {
        // In order to check if the discount code was valid, we need to
        // check the returned 'checkout' object's discount codes
        // and see if it matches the one passed in.
        const {
          discountApplications: [appliedDiscountCode],
        } = res;

        // Check if the discount code was applied to the checkout object
        if (!appliedDiscountCode || discountCode !== appliedDiscountCode.code) {
          // Invalid discount code
          return dispatch(invalidDiscountCodeApplied());
        }

        // If no error is returned and an updated 'checkout' object
        // is returned, then the discount code is valid and we
        // can update the 'checkout' object in Redux > Shop
        return dispatch(processShopifyResponse(res, applyDiscountCodeReceive));
      })
      .catch((err) =>
        dispatch(processShopifyErrorResponse(err, applyDiscountCodeFailure))
      );
  };
};

export const removeDiscountCodeReceive = (
  checkout,
  discountCode,
  variantId
) => ({
  type: Shop.REMOVE_DISCOUNT_CODE_RECEIVE,
  checkout,
  discountCode,
  variantId,
});

export const removeDiscountCodeFailure = (errors, discountCode) => {
  return (dispatch) => {
    dispatch({ type: App.APP_ERROR, errors });

    dispatch({ type: Shop.REMOVE_DISCOUNT_CODE_FAILURE, discountCode });
  };
};

export const removeDiscountCodeRequest = (
  checkoutId,
  discountCode = null,
  variantId = null
) => {
  return (dispatch) => {
    dispatch({ type: Shop.REMOVE_DISCOUNT_CODE_REQUEST });

    // https://shopify.github.io/js-buy-sdk/#removing-a-discount
    return ApiClient.getShopify()
      .checkout.removeDiscount(checkoutId)
      .then((res) =>
        dispatch(
          processShopifyResponse(
            res,
            partialRight(removeDiscountCodeReceive, discountCode, variantId)
          )
        )
      )
      .catch((err) =>
        dispatch(
          processShopifyErrorResponse(
            err,
            partialRight(removeDiscountCodeFailure, discountCode)
          )
        )
      );
  };
};

export const addProductAsGiftWithPurchaseReceive =
  (checkout, productKey) => (dispatch) => {
    dispatch({
      type: Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_RECEIVE,
      checkout,
      productKey,
    });
  };

export const addProductAsGiftWithPurchaseFailure =
  (errors, productKey) => (dispatch) => {
    dispatch({
      type: Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_FAILURE,
      productKey,
    });
    dispatch({ type: App.APP_ERROR, errors });
  };

export const addProductAsGiftWithPurchaseRequest =
  ({ checkoutId, discountCode, productKey }) =>
  (dispatch) => {
    dispatch({
      type: Shop.ADD_PRODUCT_AS_GIFT_WITH_PURCHASE_REQUEST,
      productKey,
    });
    return ApiClient.getShopify()
      .checkout.addDiscount(checkoutId, discountCode)
      .then((res) => {
        // In order to check if the discount code was valid, we need to
        // check the returned 'checkout' object's discount codes
        // and see if it matches the one passed in.
        const {
          discountApplications: [appliedDiscountCode],
        } = res;

        // Check if the discount code was applied to the checkout object
        if (!appliedDiscountCode || discountCode !== appliedDiscountCode.code) {
          // Invalid discount code
          return dispatch(invalidDiscountCodeApplied());
        }

        // If no error is returned and an updated 'checkout' object
        // is returned, then the discount code is valid and we
        // can update the 'checkout' object in Redux > Shop
        return dispatch(
          processShopifyResponse(
            res,
            partialRight(addProductAsGiftWithPurchaseReceive, productKey)
          )
        );
      })
      .catch((err) =>
        dispatch(
          processShopifyErrorResponse(
            err,
            partialRight(addProductAsGiftWithPurchaseFailure, productKey)
          )
        )
      );
  };

export const removeProductAsGiftWithPurchaseReceive =
  (productKey, checkout) => (dispatch) => {
    dispatch({
      type: Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_RECEIVE,
      productKey,
      checkout,
    });
  };

export const removeProductAsGiftWithPurchaseRequest =
  ({ checkoutId, lineItem, productKey }) =>
  (dispatch) => {
    dispatch({
      type: Shop.REMOVE_PRODUCT_AS_GIFT_WITH_PURCHASE_REQUEST,
      checkoutId,
      productKey,
      lineItem,
    });
  };

export const updateLineItemsReceive = (checkout) => ({
  type: Shop.UPDATE_LINE_ITEMS_RECEIVE,
  checkout,
});

export const updateLineItemsFailure = (errors) => ({
  type: Shop.UPDATE_LINE_ITEMS_FAILURE,
  errors,
});

export const updateLineItemsRequest =
  ({ checkoutId, lineItems }) =>
  (dispatch) => {
    dispatch({ type: Shop.UPDATE_LINE_ITEMS_REQUEST, checkoutId });

    return ApiClient.getShopify()
      .checkout.updateLineItems(checkoutId, lineItems)
      .then((checkout) =>
        dispatch(processShopifyResponse(checkout, updateLineItemsReceive))
      )
      .catch((err) =>
        dispatch(processShopifyErrorResponse(err, updateLineItemsFailure))
      );
  };

export const checkout = (webUrl) => ({
  type: Shop.CHECKOUT,
  webUrl,
});

const ShopActions = {
  showCart,
  hideCart,
  addToCartReceive,
  addToCartRequest,
  addToCartRequestFailure,
  removeFromCartReceive,
  removeFromCartRequest,
  removeFromCartRequestFailure,
  updateQuantityReceive,
  updateQuantityRequest,
  updateQuantityRequestFailure,
  fetchCheckoutFailure,
  fetchCheckoutReceive,
  fetchCheckoutRequest,
  fetchCollectionFailure,
  fetchCollectionReceive,
  fetchCollectionRequest,
  fetchSleepGoalSubCollectionsFailure,
  fetchSleepGoalSubCollectionsReceive,
  fetchSleepGoalSubCollectionsRequest,
  addProductAsGiftWithPurchaseRequest,
  addProductAsGiftWithPurchaseReceive,
  addProductAsGiftWithPurchaseFailure,
  removeProductAsGiftWithPurchaseRequest,
  removeProductAsGiftWithPurchaseReceive,
  updateLineItemsReceive,
  updateLineItemsFailure,
  updateLineItemsRequest,
};

export default ShopActions;
